import { useEffect, useMemo, useState } from 'react';

import { Deal } from '../gql/dealGql';
import { OptionTypeEnum } from '../gql/financialInfoGql';
import {
  DealMediaTypeEnum,
  File,
  Maybe,
  ProductSlug,
  TtDocumentName,
  TtJurisdiction,
  useDealPackageConfigsQuery,
  useUpdateDealNeedsElectronicSignatureVerificationMutation,
} from '../gql/generated/graphql';

import { parseFilename } from '../components/MediaCenter/utils';
import { DEAL_MEDIA_CUSTOMER_TYPES, isSignatureUploadable } from '../constants/media';
import { DealActions, DealActionsEnum } from '../libs/DealContext';
import { logger } from '../libs/Logger';
import { DealMedia } from '../types/media';
import { DEFAULT_PACKAGE_CONFIGS, findSelectedProductSlugsForSlotIndex } from '../types/products';
import {
  dealMediaToFile,
  isNotaryRequired,
  isVerifiedDigitalSignatureRequired,
  isWetSignatureRequired,
  mapTTDocumentType,
} from '../utils/media';

export interface DocumentMedia {
  mediaList: DealMedia[];
  internalMediaList: DealMedia[];
  customerMediaList: DealMedia[];
  unverifiedRequiredMediaList: DealMedia[];
  missingRequiredMediaList: DealMedia[];
  signatureFileList: File[];
}

type UseDocumentMediaProps = {
  deal: Deal;
  dealDispatch: (value: DealActions) => void;
  media?: Maybe<Maybe<DealMedia>>[] | null;
  jurisdiction?: Maybe<TtJurisdiction> | null;
};

const useDocumentMedia = ({ deal, dealDispatch, media, jurisdiction }: UseDocumentMediaProps) => {
  const [mediaList, setMediaList] = useState<DealMedia[]>([]);

  const [
    updateDealNeedsElectronicSignatureVerification,
    { loading: updateDealNeedsElectronicSignatureVerificationLoading },
  ] = useUpdateDealNeedsElectronicSignatureVerificationMutation();

  const { internal: internalMediaList, customer: customerMediaList } = useMemo(
    () =>
      mediaList.reduce(
        (acc, mediaItem) => {
          if (mediaItem.type && !DEAL_MEDIA_CUSTOMER_TYPES.includes(mediaItem.type)) {
            acc.internal.push(mediaItem);
          } else {
            acc.customer.push(mediaItem);
          }
          return acc;
        },
        { internal: [], customer: [] } as {
          internal: DealMedia[];
          customer: DealMedia[];
        },
      ),
    [mediaList],
  );

  const requiredMediaList = useMemo(
    (): DealMedia[] =>
      jurisdiction?.products?.items?.reduce((requiredMediaListAcc, product) => {
        const productDocuments =
          product?.documents?.items
            ?.filter(
              (ttDoc) =>
                // Only add known document types.
                Object.values(TtDocumentName).includes(ttDoc?.type?.name as TtDocumentName) &&
                !requiredMediaListAcc.some(
                  (dealMedia) =>
                    // Don't add the same document twice.
                    // Need to map the type to compare (e.g. `PowerOfAttorney` can be present in multiple products but is mapped to a different type if inside `Payoff`)
                    dealMedia?.type === mapTTDocumentType(product?.type, ttDoc?.type?.name),
                ),
            )
            .map((ttDoc) => ({
              type: mapTTDocumentType(product?.type, ttDoc?.type?.name),
              verified_digital_signature_required: isVerifiedDigitalSignatureRequired(
                ttDoc?.validations,
              ),
              wet_signature_required: isWetSignatureRequired(ttDoc?.validations),
              notary_required: isNotaryRequired(ttDoc?.validations),
            })) ?? [];

        requiredMediaListAcc.push(...productDocuments);
        return requiredMediaListAcc;
      }, [] as DealMedia[]) ?? [],
    [jurisdiction],
  );

  const needsElectronicSignatureVerification = useMemo(
    () =>
      requiredMediaList.some(
        (m) =>
          m.type === DealMediaTypeEnum.ElectronicSignatureAttestation ||
          m.type === DealMediaTypeEnum.ElectronicSignatureCertification,
      ),
    [requiredMediaList],
  );

  // `verified` null means the user has already uploaded the document but it has not been verified yet.
  const unverifiedRequiredMediaList = useMemo(
    () =>
      customerMediaList.filter((m) => !!m.type && (m.verified === null || m.verified === false)),
    [customerMediaList],
  );

  const missingRequiredMediaList = useMemo(
    () => customerMediaList.filter((m) => !!m.type && !m.key),
    [customerMediaList],
  );

  const dealId = deal?.id ? Number(deal.id) : 0;
  const { data: configsData } = useDealPackageConfigsQuery({
    variables: { dealId },
    skip: !dealId,
    fetchPolicy: 'cache-and-network',
  });
  const configs = configsData?.dealPackageConfigs ?? [];
  const effectiveConfigs = configs.length > 0 ? configs : DEFAULT_PACKAGE_CONFIGS;

  const selectedProductSlugs = useMemo(() => {
    const slugsFromSlotIndex = findSelectedProductSlugsForSlotIndex(
      effectiveConfigs,
      deal.selected_package_slot_index,
    );
    if (slugsFromSlotIndex != null) {
      return slugsFromSlotIndex;
    }

    // Legacy fallback: derive product slugs from option_type for deals
    // that predate the package slot system.
    const optionType = deal.financial_info?.option_type;
    switch (optionType) {
      case OptionTypeEnum.VscPlusGap:
        return [ProductSlug.Vsc, ProductSlug.Gap];
      case OptionTypeEnum.VSC:
        return [ProductSlug.Vsc];
      case OptionTypeEnum.Gap:
        return [ProductSlug.Gap];
      default:
        return [];
    }
  }, [effectiveConfigs, deal.selected_package_slot_index, deal.financial_info?.option_type]);

  const signatureFileList: File[] = useMemo(
    () =>
      mediaList
        .filter(
          (mediaItem) =>
            parseFilename(mediaItem.key ?? '').isPdf &&
            mediaItem.type &&
            isSignatureUploadable(mediaItem.type, selectedProductSlugs, mediaItem.key),
        )
        .map(dealMediaToFile),
    [mediaList, selectedProductSlugs],
  );

  const handleUpdateDealNeedsElectronicSignatureVerification = async (
    newNeedsElectronicSignatureVerification: boolean,
  ) => {
    if (!deal?.id) {
      return;
    }

    try {
      const result = await updateDealNeedsElectronicSignatureVerification({
        variables: {
          deal_id: deal.id,
          needs_electronic_signature_verification: newNeedsElectronicSignatureVerification,
        },
      });

      if (result.data?.updateDealNeedsElectronicSignatureVerification) {
        dealDispatch({
          type: DealActionsEnum.UpdateDeal,
          payload: {
            needs_electronic_signature_verification:
              !!result.data.updateDealNeedsElectronicSignatureVerification
                .needs_electronic_signature_verification,
          },
        });
      }
    } catch (e) {
      logger.error('useDocumentMedia.ts', '', null, e);
    }
  };

  useEffect(() => {
    if (
      !updateDealNeedsElectronicSignatureVerificationLoading &&
      needsElectronicSignatureVerification !== deal.needs_electronic_signature_verification
    ) {
      handleUpdateDealNeedsElectronicSignatureVerification(needsElectronicSignatureVerification);
    }
  }, [needsElectronicSignatureVerification]);

  // TODO: Finish this refactor and try to move it to backend
  const extractDocumentMedia = async () => {
    const newMedia: DealMedia[] =
      media
        ?.filter((dm) => dm !== null && dm !== undefined)
        .map((dm) => {
          const matchedRequiredDocument = requiredMediaList.find((rm) => rm.type === dm.type);
          if (matchedRequiredDocument) {
            return {
              ...dm,
              ...matchedRequiredDocument,
            };
          }
          return dm;
        }) ?? [];

    const uploadedTypes = media?.map((m) => m?.type) ?? [];
    const notUploadedRequiredMedia = requiredMediaList
      .filter((m) => !uploadedTypes.includes(m.type))
      .map((m) => ({
        ...m,
        has_verified_digital_signature: false,
        has_wet_signature: false,
        is_notarized: false,
      }));

    setMediaList([...newMedia, ...notUploadedRequiredMedia]);
  };

  useEffect(() => {
    extractDocumentMedia();
  }, [media, requiredMediaList]);

  const documentMedia: DocumentMedia = {
    mediaList,
    internalMediaList,
    customerMediaList,
    unverifiedRequiredMediaList,
    missingRequiredMediaList,
    signatureFileList,
  };

  return { documentMedia };
};

export default useDocumentMedia;
